// Admin-only list of external call-center accounts (#1261): binding +
// warning badge per row, a create action, a rebind control, and the
// reused user-lifecycle actions (deactivate / reactivate / reset).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CallCenterAccount } from '@magimp/shared';
import { useCallCenterAccounts } from '@magimp/shared-hooks';
import { Button, EmptyState, ErrorState, LoadingState, ToolbarActions } from '../../ui/index.js';
import { errorMessage } from '../../api/errors.js';
import { CallCenterAccountCreateModal } from './CallCenterAccountCreateModal.js';
import { CallCenterBindingModal } from './CallCenterBindingModal.js';
import { AccountLifecycleAction, type AccountLifecycleKind } from './AccountLifecycleAction.js';

type LifecycleTarget = { userId: string; kind: AccountLifecycleKind };

export function CallCenterAccountsPanel() {
  const { t } = useTranslation(['callCenterAdmin', 'common']);
  const accountsQ = useCallCenterAccounts();
  const [createOpen, setCreateOpen] = useState(false);
  const [rebindTarget, setRebindTarget] = useState<CallCenterAccount | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleTarget | null>(null);

  return (
    <section data-testid="cc-accounts-panel">
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>{t('callCenterAdmin:accounts.title')}</h2>
        <ToolbarActions>
          <Button kind="primary" onClick={() => setCreateOpen(true)}>
            {t('callCenterAdmin:accounts.create')}
          </Button>
        </ToolbarActions>
      </header>

      {accountsQ.isPending && <LoadingState text={t('common:states.loading')} />}
      {accountsQ.isError && (
        <ErrorState
          title={t('common:states.errorTitle')}
          text={errorMessage(accountsQ.error)}
          onRetry={() => accountsQ.refetch()}
        />
      )}
      {accountsQ.data && accountsQ.data.length === 0 && (
        <EmptyState title={t('callCenterAdmin:accounts.empty')} />
      )}
      {accountsQ.data && accountsQ.data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--fg-2)', fontSize: 12 }}>
              <th>{t('callCenterAdmin:accounts.colName')}</th>
              <th>{t('callCenterAdmin:fields.email')}</th>
              <th>{t('callCenterAdmin:accounts.colBinding')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accountsQ.data.map((acc) => (
              <AccountRow
                key={acc.userId}
                account={acc}
                onRebind={() => setRebindTarget(acc)}
                onDeactivate={() => setLifecycle({ userId: acc.userId, kind: 'deactivate' })}
                onReactivate={() => setLifecycle({ userId: acc.userId, kind: 'reactivate' })}
                onReset={() => setLifecycle({ userId: acc.userId, kind: 'reset' })}
              />
            ))}
          </tbody>
        </table>
      )}

      {createOpen && <CallCenterAccountCreateModal onClose={() => setCreateOpen(false)} />}
      {rebindTarget && (
        <CallCenterBindingModal account={rebindTarget} onClose={() => setRebindTarget(null)} />
      )}
      {lifecycle && (
        <AccountLifecycleAction
          userId={lifecycle.userId}
          kind={lifecycle.kind}
          onClose={() => setLifecycle(null)}
        />
      )}
    </section>
  );
}

interface AccountRowProps {
  account: CallCenterAccount;
  onRebind: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onReset: () => void;
}

function AccountRow({ account, onRebind, onDeactivate, onReactivate, onReset }: AccountRowProps) {
  const { t } = useTranslation(['callCenterAdmin', 'common']);
  return (
    <tr style={{ borderTop: '1px solid var(--border-1)', fontSize: 13 }}>
      <td style={{ padding: '8px 0' }}>{account.name}</td>
      <td>{account.email}</td>
      <td>
        {account.binding ? account.binding.sourceDisplayName : t('callCenterAdmin:binding.none')}
        {account.bindingWarning && (
          <span
            data-testid="cc-binding-warning"
            style={{
              marginLeft: 8,
              padding: '1px 6px',
              borderRadius: 'var(--r-1)',
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
              fontSize: 11,
            }}
          >
            {t('callCenterAdmin:binding.warning')}
          </span>
        )}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Button kind="ghost" onClick={onRebind}>
          {t('callCenterAdmin:actions.rebind')}
        </Button>
        {account.active ? (
          <Button kind="ghost" onClick={onDeactivate}>
            {t('common:actions.deactivate')}
          </Button>
        ) : (
          <Button kind="ghost" onClick={onReactivate}>
            {t('common:actions.reactivate')}
          </Button>
        )}
        <Button kind="ghost" onClick={onReset}>
          {t('common:actions.resetPassword')}
        </Button>
      </td>
    </tr>
  );
}
